import { FilterItemValues, FilterKey } from "../types/filters";
import {
    getEffectiveStatus,
    hasRecentGps,
    isInService,
    hasValidDevice,
    isAdminOrOperator,
    isAppUpdateAvailable,
    isLoggedIn,
    isTrackingOn,
    Member,
} from "../types/member";
import { isVisibleLiveBadge, MemberLiveBadge } from "../types/memberLiveBadge";
import { Day, Team } from "../types/core";
import { hhmmToMinutes, zonedMoment } from "./zonedTime";
import { resolveWorkingHours, windowForDay } from "../components/dispatch/planner/plannerUtils";

export const PEOPLE_FILTER = {
    STATUS: "status",
    TEAM: "team",
    SKILL: "skill",
    ROLE: "role",
    JOBS: "jobs",
    WORKING: "working",
    ATTENTION: "attention",
    /** Live's quick toggles: reporting (logged in, tracking and location on, seen in the last 18 hours), and in service (logged in, tracking, not off duty) */
    ACTIVE: "active",
    SERVICE: "service",
} as const;

/** The one value of the yes/no filters behind Live's quick toggles. */
export const PEOPLE_YES = "yes";

/** The status values in the order the bar shows them, worded like the member status line. */
export const PEOPLE_STATUS = {
    MOVING: "moving",
    AT_JOB: "job",
    AT_PLACE: "place",
    STATIONARY: "stationary",
    NOT_REPORTING: "silent",
    OFF_DUTY: "offduty",
} as const;

export const PEOPLE_ATTENTION = {
    INVITED: "invited",
    LOGGED_OUT: "loggedout",
    TRACKING_OFF: "trackingoff",
    LOCATION_OFF: "locationoff",
    BACKGROUND_OFF: "background",
    BATTERY_SAVE: "batterysave",
    BATTERY_LOW: "batterylow",
    APP_UPDATE: "appupdate",
} as const;

export const NO_TEAM = "none";
const BATTERY_LOW_PERCENT = 20;

/**
 * The one status a person is filtered under. Follows the status line: a phone that cannot report is
 * "not reporting" whatever its last badge said, then a job beats a place beats moving beats stationary.
 * Invited people and desk staff have no status; the "Needs attention" filter finds the invited.
 */
export function peopleStatus(member: Member, badge: MemberLiveBadge | undefined): string | undefined {
    const status = getEffectiveStatus(member, true, badge);
    switch (status) {
        case "offduty":
            return PEOPLE_STATUS.OFF_DUTY;
        case "loggedout":
        case "trackingoff":
        case "locationservicesoff":
            return PEOPLE_STATUS.NOT_REPORTING;
        case "invited":
        case "admin-or-operator":
            return undefined;
    }
    if (badge?.kind === "job") {
        return PEOPLE_STATUS.AT_JOB;
    }
    if (badge?.kind === "place" && isVisibleLiveBadge(badge)) {
        return PEOPLE_STATUS.AT_PLACE;
    }
    if (status === "on-the-way") {
        return PEOPLE_STATUS.MOVING;
    }
    if (status === "idling") {
        return PEOPLE_STATUS.STATIONARY;
    }
    return undefined;
}

/** What needs a look on someone's phone; the same findings as the status line, plus battery and app. */
export function peopleAttention(member: Member): string[] {
    // desk staff whose phone reports nothing and people off duty have nothing to look at
    const desk = !member.states || (isAdminOrOperator(member) && !hasRecentGps(member));
    if (desk || getEffectiveStatus(member) === "offduty") {
        return [];
    }
    if (!hasValidDevice(member)) {
        return [PEOPLE_ATTENTION.INVITED];
    }
    const found: string[] = [];
    const loggedOut = !isLoggedIn(member);
    if (loggedOut) {
        found.push(PEOPLE_ATTENTION.LOGGED_OUT);
    } else if (!isTrackingOn(member)) {
        found.push(PEOPLE_ATTENTION.TRACKING_OFF);
    }
    if (!member.states.location_services) {
        found.push(PEOPLE_ATTENTION.LOCATION_OFF);
    }
    if (member.states.background_location === false) {
        found.push(PEOPLE_ATTENTION.BACKGROUND_OFF);
    }
    if (member.states.battery_save) {
        found.push(PEOPLE_ATTENTION.BATTERY_SAVE);
    }
    if (member.battery_level > 0 && member.battery_level < BATTERY_LOW_PERCENT && !member.battery_plugged && hasRecentGps(member, 2 * 60 * 60 * 1000)) {
        found.push(PEOPLE_ATTENTION.BATTERY_LOW);
    }
    if (isAppUpdateAvailable(member)) {
        found.push(PEOPLE_ATTENTION.APP_UPDATE);
    }
    return found;
}

/**
 * Inside today's working hours, the way the planner reads them: own hours, else the team's, else the
 * default. Today and the time are the account's clock.
 */
export function isWorkingNow(member: Member, teamsById: Map<number, Team>, now: number, timezone?: string): boolean {
    const team = member.teams?.map(id => teamsById.get(id)).find(t => !!t?.working_hours);
    const moment = zonedMoment(now, timezone);
    const window = windowForDay(resolveWorkingHours(member, team), member.working_hours_overrides, moment.day as Day);
    if (!window || (window.start === 0 && window.end === 0)) {
        return false;
    }
    return moment.minutes >= hhmmToMinutes(window.start) && moment.minutes < hhmmToMinutes(window.end);
}

/** Skills are free text; "HVAC " and "hvac" are one skill to a filter. */
export function skillKey(skill: string): string {
    return skill.trim().toLowerCase();
}

export type PeopleFilterContext = {
    badge?: MemberLiveBadge;
    /** usernames with jobs on the shown day; only read when the jobs filter is asked for */
    assignedUsernames?: Set<string>;
    teamsById?: Map<number, Team>;
    now: number;
    /** the account's time zone; the browser's when missing */
    timezone?: string;
};

/** Active is black and white: a phone that is logged out or has tracking or location off is never active, however recent its last fix. */
function isReporting(member: Member): boolean {
    return isLoggedIn(member) && isTrackingOn(member) && !!member.states.location_services && !!hasRecentGps(member);
}

/** A person's values for the filters in `keys` (all of them when omitted). */
export function peopleFilterValues(member: Member, context: PeopleFilterContext, keys?: FilterKey[]): FilterItemValues {
    const wants = (key: string) => !keys || keys.includes(key);
    const values: FilterItemValues = {};
    if (wants(PEOPLE_FILTER.STATUS)) {
        const status = peopleStatus(member, context.badge);
        values[PEOPLE_FILTER.STATUS] = status ? [status] : [];
    }
    if (wants(PEOPLE_FILTER.TEAM)) {
        values[PEOPLE_FILTER.TEAM] = member.teams?.length ? member.teams.map(String) : [NO_TEAM];
    }
    if (wants(PEOPLE_FILTER.SKILL)) {
        values[PEOPLE_FILTER.SKILL] = (member.skills ?? []).map(skillKey).filter(s => s.length > 0);
    }
    if (wants(PEOPLE_FILTER.ROLE)) {
        values[PEOPLE_FILTER.ROLE] = member.role ? [member.role] : [];
    }
    if (wants(PEOPLE_FILTER.JOBS)) {
        values[PEOPLE_FILTER.JOBS] = [context.assignedUsernames?.has(member.username) ? "has" : "none"];
    }
    if (wants(PEOPLE_FILTER.WORKING)) {
        values[PEOPLE_FILTER.WORKING] = [isWorkingNow(member, context.teamsById ?? new Map(), context.now, context.timezone) ? "yes" : "no"];
    }
    if (wants(PEOPLE_FILTER.ATTENTION)) {
        values[PEOPLE_FILTER.ATTENTION] = peopleAttention(member);
    }
    if (wants(PEOPLE_FILTER.ACTIVE)) {
        values[PEOPLE_FILTER.ACTIVE] = member.states && isReporting(member) ? [PEOPLE_YES] : [];
    }
    if (wants(PEOPLE_FILTER.SERVICE)) {
        values[PEOPLE_FILTER.SERVICE] = member.states && isInService(member) ? [PEOPLE_YES] : [];
    }
    return values;
}
